import { useContext } from "react";
import classnames from "classnames";
import { useSelector } from "react-redux";

import ConfigContext from "context/ConfigContext";
import FinderResultsContext from "context/FinderResultsContext";
import MapContext from "context/MapContext";
import { Store } from "custom-types/Store";
import { useEventTracker } from "hooks/useEventTracker";
import useSplit from "hooks/useSplit";
import useTrackImpression from "hooks/useTrackImpression";
import { getDomainCountryCode } from "redux/selectors/config";
import { hasLeaflyDelivery, hasLeaflyPickup } from "utils/dispensaryUtils";
import { getRoundedDistance } from "utils/distance";
import {
  getEventLabel,
  getMerchId,
  trackStoreImpression,
} from "utils/eventTracking";
import { getStoreUrl } from "utils/storeUrlUtils";
import { Action, Category } from "utils/trackEvent";

import StoreCard from "./StoreCard";

type Props = {
  store: Store;
  index: number;
  compact?: boolean;
  id: number | string;
  hideOnMobile?: boolean;
  retailType?: string;
};

const StoreCardWithData = ({
  store,
  index,
  compact,
  id,
  hideOnMobile,
  retailType,
}: Props) => {
  const { storeRoute } = useContext(ConfigContext);

  const countryCode = useSelector(getDomainCountryCode);

  const { strainFilters, availableFilters } = useContext(FinderResultsContext);
  const { publishEvent } = useEventTracker();

  const {
    dispatch: { track },
  } = useSplit();

  const {
    handleMouseOverStore,
    handleMouseAwayStore,
    hoveredStore,
    selectedStore,
  } = useContext(MapContext);

  const hasDelivery = hasLeaflyDelivery(store);
  const hasPickup = hasLeaflyPickup(store);
  const hasNeither = !hasPickup && !hasDelivery;

  const trackingData = {
    label: store.name,
    origination: store.tier < 5 ? `List ${store.tier + 1}` : "List",
    ...store,
    customDimensions: {
      merchandisingCampaignId: getMerchId(store, "platinumplacement"),
      ...(store?.deals && store.deals[0] ? { dealId: store.deals[0].id } : {}),
      dispensaryDistance: getRoundedDistance(store.distanceMi, countryCode),
    },
  };

  const [setRef] = useTrackImpression(
    () => trackStoreImpression({ ...trackingData }),
    {
      threshold: 0.5,
    }
  );

  const handleCardClick = () => {
    if (store.tier < 900) {
      track("webFinder_storeCardClick_frontend", {
        retailType: store.retailType,
        row: index,
        tier: store.tier,
      });
      publishEvent({
        action: Action.click,
        category: Category.finder,
        label: getEventLabel(
          trackingData.origination,
          trackingData.tier,
          trackingData.mapMarkerLevel
        ) as string,
        merchandisingCampaignId:
          trackingData.customDimensions.merchandisingCampaignId,
        ...(trackingData.customDimensions?.dealId
          ? { dealId: trackingData.customDimensions?.dealId }
          : {}),
      });
    } else {
      publishEvent({
        action: "click",
        category: "finder",
        label: "info listing",
      });
    }
  };

  const storeParams = {
    availableFilters,
    hasMenu: (store?.reservableMenuItemCount && !hasNeither) ?? false,
    storeRoute,
    storeSlug: store.slug,
    ...((strainFilters?.strain || strainFilters?.product_type) && {
      strainFilters,
    }),
  };
  const isHovered = hoveredStore === store.id;
  const isSelected = selectedStore?.id === store.id;

  return (
    <StoreCard
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
      ref={setRef as any}
      compact={compact ?? false}
      retailType={retailType}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
      href={getStoreUrl(storeParams as any)}
      id={id}
      onClick={handleCardClick}
      ctaOnClick={handleCardClick}
      onMouseEnter={() => handleMouseOverStore(store.id)}
      onMouseLeave={handleMouseAwayStore}
      platinumDeals="on_plat"
      selected={isSelected}
      store={store}
      hideOnMobile={hideOnMobile}
      className={classnames("border border-light-grey", {
        "my-sm": !compact,
        "shadow-none": !isHovered && !isSelected,
      })}
    />
  );
};

export default StoreCardWithData;
